import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  contentChild,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { MatCard } from '@angular/material/card';
import { Router } from '@angular/router';
import { SideBarService } from '@vectronic/util';
import { ResizableModule, ResizeEvent } from 'angular-resizable-element';
import { LayoutResizeEventService } from '../../../services/sideMenuLayoutEvent/layout-resize-event.service';

@Component({
  selector: 'vectronic-layout-with-sidebar',
  imports: [CommonModule, MatCard, ResizableModule],
  templateUrl: './layout-with-sidebar.component.html',
  styleUrl: './layout-with-sidebar.component.scss',
})
export class LayoutWithSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly defaultSidebarWidthInPercent = 50;
  private readonly maxSidebarWidthInPercent = 97;
  private readonly multiSidebarWidthInPx = 300;
  private layoutResizeEventService = inject(LayoutResizeEventService);
  private router = inject(Router);
  private readonly leftCollapsedState = signal(false);
  private readonly rightCollapsedState = signal(false);

  hasSideBar = input<boolean>();
  withoutVerticalPadding = input<boolean>();
  isDashboardPage = input<boolean>();
  hasMultipleSideBar = input<boolean>();
  useFixedWidthForMultipleSideBar = input<boolean>(true);
  isMultiResizable = input<boolean>(false);
  hideSidebarCard = input<boolean>();
  initialSidebarSizeInPercent = input<number>();
  resized = output<number>();

  applyWidthEffect = effect(() => this.applyModeWidth());

  // slots
  table = contentChild<any>('table');
  leftSide = contentChild<any>('leftSide');
  rightSide = contentChild<any>('rightSide');
  multi = contentChild<any>('multi');
  single = contentChild<any>('single');

  // NEW: toggle button slots (shown when collapsed)
  leftToggle = contentChild<any>('leftToggle');
  rightToggle = contentChild<any>('rightToggle');

  // right column config
  rightMenuWidthPx = input<number>(320);

  // NEW: parent-driven collapsed flags
  leftCollapsed = input<boolean>();
  rightCollapsed = input<boolean>();

  style: { width: string };
  mapStyle: { width: string };
  sideBarWidth = 700;

  private sideBarService = inject(SideBarService);
  // NEW: allow parent to decide if menu exists
  hasLeftMenu = input(false);
  hasRightMenu = input(false);

  syncCollapsedInputsEffect = effect(() => {
    const leftCollapsed = this.leftCollapsed();
    if (leftCollapsed !== undefined) {
      this.setLeftCollapsed(leftCollapsed);
    }

    const rightCollapsed = this.rightCollapsed();
    if (rightCollapsed !== undefined) {
      this.setRightCollapsed(rightCollapsed);
    }
  });

  shouldShowLeftSide() {
    return this.hasLeftMenu() && !this.leftCollapsedState();
  }

  shouldShowRightSide() {
    // hide the right column if details sidebar is open
    return !this.rightCollapsedState() && !this.hasSideBar() && this.hasRightMenu();
  }

  shouldShowRightToggle() {
    return this.rightCollapsedState() && !this.hasSideBar();
  }

  private shouldUseFixedWidthForMultipleSideBar() {
    return (
      this.hasMultipleSideBar() &&
      this.useFixedWidthForMultipleSideBar() &&
      !this.isMultiResizable()
    );
  }

  private applyModeWidth() {
    if (!this.hasSideBar?.()) {
      this.resetSidebarState();
      return;
    }

    this.sideBarService.isOn.set(true);
    if (this.shouldUseFixedWidthForMultipleSideBar()) {
      this.sideBarWidth = this.multiSidebarWidthInPx;
      this.style = { width: `${this.multiSidebarWidthInPx}px` };
      this.mapStyle = { width: `calc(100% - ${this.multiSidebarWidthInPx}px)` };
      this.sideBarService.width.set(this.sideBarWidth);
      this.layoutResizeEventService.resizeEndedEvent$.next();
      return;
    }

    const viewportW = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const defaultPercent = this.hasMultipleSideBar() ? 35 : this.defaultSidebarWidthInPercent;
    const percent = this.getInitialSidebarSizeInPercent(defaultPercent);
    this.sideBarWidth = (viewportW * percent) / 100;
    this.style = { width: `${percent}%` };
    this.mapStyle = { width: `calc(100% - ${percent}%)` };
    this.onResizeEnd();
  }

  validate({ rectangle }: ResizeEvent) {
    const MIN_DIMENSIONS_PX = 50;
    const { width, height } = rectangle;
    return !width || !height || (width >= MIN_DIMENSIONS_PX && height >= MIN_DIMENSIONS_PX);
  }

  onResizeEnd() {
    if (this.shouldUseFixedWidthForMultipleSideBar()) return;

    this.sideBarWidth = Math.min(this.sideBarWidth, this.getMaxSidebarWidthInPx());
    this.sideBarService.width.set(this.sideBarWidth);
    const viewportW = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const sidebarWidthPercent = (this.sideBarWidth * 100) / viewportW;
    this.storeSidebarWidthInPercent(sidebarWidthPercent);
    this.resized.emit(sidebarWidthPercent);
    this.layoutResizeEventService.resizeEndedEvent$.next();
  }

  onResizing(event: ResizeEvent) {
    if (this.shouldUseFixedWidthForMultipleSideBar()) return;

    const nextWidth = Math.min(event.rectangle.width, this.getMaxSidebarWidthInPx());
    this.style = { width: `${nextWidth}px` };
    this.mapStyle = { width: `calc(100% - ${nextWidth}px)` };
    this.sideBarWidth = nextWidth;
  }

  ngOnInit() {
    if (!this.hasSideBar?.()) {
      this.resetSidebarState();
      return;
    }

    if (this.shouldUseFixedWidthForMultipleSideBar()) {
      this.sideBarWidth = this.multiSidebarWidthInPx;
      this.style = { width: `${this.multiSidebarWidthInPx}px` };
      this.mapStyle = { width: `calc(100% - ${this.multiSidebarWidthInPx}px)` };
      return;
    }

    const defaultPercent = this.hasMultipleSideBar() ? 35 : this.defaultSidebarWidthInPercent;
    const initialSidebarSizeInPercent = this.getInitialSidebarSizeInPercent(defaultPercent);
    this.style = { width: `${initialSidebarSizeInPercent}%` };
    this.mapStyle = { width: `calc(100% - ${initialSidebarSizeInPercent}%)` };
  }

  ngAfterViewInit() {
    this.initializeCollapsedState();
    this.applyModeWidth();
  }

  ngOnDestroy() {
    this.resetSidebarState();
  }

  private getInitialSidebarSizeInPercent(defaultPercent: number) {
    const storageKey = this.getSidebarWidthStorageKey();
    if (!storageKey || typeof localStorage === 'undefined') {
      return this.stopSidebarWidthAt(this.initialSidebarSizeInPercent() ?? defaultPercent);
    }

    const storedWidth = Number(localStorage.getItem(storageKey));
    if (Number.isFinite(storedWidth) && storedWidth > 0 && storedWidth <= 100) {
      return this.stopSidebarWidthAt(storedWidth);
    }

    return this.stopSidebarWidthAt(this.initialSidebarSizeInPercent() ?? defaultPercent);
  }

  private storeSidebarWidthInPercent(sidebarWidthInPercent: number) {
    const storageKey = this.getSidebarWidthStorageKey();
    if (!storageKey || typeof localStorage === 'undefined') return;

    localStorage.setItem(storageKey, String(sidebarWidthInPercent));
  }

  private getSidebarWidthStorageKey() {
    return this.getRouteStorageKey('sidebar-width-percent');
  }

  private stopSidebarWidthAt(widthPercent: number) {
    return Math.min(widthPercent, this.maxSidebarWidthInPercent);
  }

  private getMaxSidebarWidthInPx() {
    const viewportW = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    return (viewportW * this.maxSidebarWidthInPercent) / 100;
  }

  setLeftCollapsed(collapsed: boolean) {
    this.leftCollapsedState.set(collapsed);
    this.storeCollapsedState('left-panel-collapsed', collapsed);
  }

  setRightCollapsed(collapsed: boolean) {
    this.rightCollapsedState.set(collapsed);
    this.storeCollapsedState('right-panel-collapsed', collapsed);
  }

  private initializeCollapsedState() {
    if (this.leftCollapsed() === undefined && this.hasLeftMenu() && this.leftToggle()) {
      this.leftCollapsedState.set(this.getStoredCollapsedState('left-panel-collapsed'));
    }

    if (this.rightCollapsed() === undefined && this.hasRightMenu() && this.rightToggle()) {
      this.rightCollapsedState.set(this.getStoredCollapsedState('right-panel-collapsed'));
    }
  }

  private getStoredCollapsedState(storageSuffix: string) {
    const storageKey = this.getRouteStorageKey(storageSuffix);
    if (!storageKey || typeof localStorage === 'undefined') {
      return false;
    }

    return localStorage.getItem(storageKey) === 'true';
  }

  private storeCollapsedState(storageSuffix: string, collapsed: boolean) {
    const storageKey = this.getRouteStorageKey(storageSuffix);
    if (!storageKey || typeof localStorage === 'undefined') return;

    localStorage.setItem(storageKey, String(collapsed));
  }

  private getRouteStorageKey(suffix: string) {
    const currentUrl = this.router.url?.split('?')[0]?.split('#')[0];
    if (!currentUrl) return undefined;

    const normalizedRoute = currentUrl.replace(/^\/+|(?<!\/)\/+$/g, '').replace(/\//g, '-');
    return `${normalizedRoute || 'root'}-${suffix}`;
  }

  private resetSidebarState() {
    this.sideBarWidth = 0;
    this.style = { width: '0px' };
    this.mapStyle = { width: '100%' };
    this.sideBarService.width.set(0);
    this.sideBarService.isOn.set(false);
  }
}
